import * as ROUTES from 'Routes'
import { useContext } from 'react'
import { ThemeContext } from 'modules/Theme'
import { ROOT_URL } from 'gatsby-env-variables'
import classNames from 'classnames'
import Text from 'components/Text'
import Button from 'components/Button'
import ImgStoryblok from 'components/ImgStoryblok'
import If from '@mobi/components/If'
import CountdownTimer from 'components/CountdownTimer'
import { Form, Formik } from 'formik'
import cpfMask from '@mobi/utils/masks/cpf'
import cnpjMask from '@mobi/utils/masks/cnpj'
import { InputText } from 'components/Fields'
import { navigate } from 'gatsby'
import useMediaQuery from 'hooks/useMediaQuery'
import useCountdownTimer from 'hooks/useCountdownTimer'
import removeSpecialChars from '@mobi/utils/formatters/removeSpecialChars'
import storage from 'utils/storage'
import './LandingHeroHoliday.scss'
import analytics from '@mobi/libraries/analytics'
import Carousel from './Carousel'
import { handlePerformanceMark } from 'utils/trackings/newRelicEvents'
import useHasMounted from 'hooks/useHasMounted'

const EMPHASIS_REGEX = /({{(\w|\s|[áâãéêíóôõúçñÁÀÂÃÉÍÓÔÕÚÇ$-?!%])+}})/gi

const HeroBanner = ({ blok, location, isInCarrossel, firstBanner }) => {
  const isDesktop = useMediaQuery('desktop')

  const { theme } = useContext(ThemeContext)
  const isRecoveryOrigin = theme === 'recovery'
  const { hasMounted } = useHasMounted()

  const countdownStartDate = new Date(
    `${blok?.countdown[0]?.start_date} GMT-0300`
  ).getTime()

  const countdownEndDate = new Date(
    `${blok?.countdown[0]?.end_date} GMT-0300`
  ).getTime()

  const targetDate = useCountdownTimer(countdownEndDate)
  const isCountDownRunning = targetDate?.some((value) => value > 0)

  const renderText = ({ text, textColor, subText = false }) => {
    if (!text) return <></>

    let textToRender = text

    if (blok?.emphasis_color) {
      const isEmphasis = (text) => text?.match(EMPHASIS_REGEX)

      const splittedTextWithEmphasis = text.split(EMPHASIS_REGEX)

      const filteredEmphasisText = splittedTextWithEmphasis
        .map((text, index) => {
          if (index === 0) return text
          if (isEmphasis(splittedTextWithEmphasis[index - 1])) return
          return text
        })
        .filter((t) => t)

      const renderEmphasisText = (text, index) => {
        const emphasisText = text.replace(/({{|}})/gi, '')

        if (emphasisText === 'newline') return <br key={`breakLine${index}`} />

        return (
          <span
            className="emphasis-texts"
            style={{ color: blok?.emphasis_color }}
            key={`emphasisText${index}`}
          >
            {emphasisText}
          </span>
        )
      }

      textToRender = filteredEmphasisText.map((text, index) => {
        if (isEmphasis(text)) {
          return renderEmphasisText(text, index)
        }

        return text
      })
    }

    return (
      <div
        className={classNames('inline no-wrap', {
          title_text: !subText,
          sub_text: subText,
        })}
        style={{ color: textColor }}
      >
        {textToRender}
      </div>
    )
  }

  const blokBgImages = {
    desktop: blok?.background_desktop?.filename?.replace(
      'https://a.storyblok.com',
      'https://assets.renegocie.gruporecovery.com'
    ),
    mobile: blok?.background_mobile?.filename?.replace(
      'https://a.storyblok.com',
      'https://assets.renegocie.gruporecovery.com'
    ),
  }
  const currentBlokImage = isDesktop
    ? blokBgImages.desktop
    : blokBgImages.mobile

  return (
    <>
      <div
        className={classNames('landing-hero-holiday', {
          'with-disclamer': blok?.disclamer_text,
        })}
        style={{
          background: blok?.background_color || '#fff',
        }}
        data-test="container-hero-holiday"
      >
        <ImgStoryblok
          src={`${currentBlokImage}`}
          preload={!isDesktop}
          alt={blok?.background_alt}
          pictureProps={{
            className: 'landing-hero-holiday__background',
            role: 'presentation',
            'aria-hidden': 'true',
          }}
        />

        <div
          className="left-part"
          style={
            !isDesktop
              ? {
                  background: blok?.background_color || '#fff',
                }
              : {}
          }
        >
          {isDesktop && (
            <Text
              h2
              size={1}
              className="label-text"
              style={{
                color: blok?.desktop_title_color || '#000000',
              }}
            >
              {blok?.desktop_title || 'negocie dívidas online'}
            </Text>
          )}
          <div className="main-text">
            <Text h1 size={3}>
              {renderText({
                text: blok?.title_line1_text,
                textColor: blok?.title_line1_color,
              })}
            </Text>

            <Text p size={3}>
              {renderText({
                text: blok?.title_line2_text,
                textColor: blok?.title_line2_color,
                subText: true,
              })}
            </Text>
          </div>

          {hasMounted && (
            <If
              condition={isRecoveryOrigin && isCountDownRunning}
              renderIf={
                <If
                  condition={Date.now() >= countdownStartDate}
                  renderIf={
                    <div
                      className="lp-countdown"
                      style={{
                        background: blok?.countdown[0]?.background_color,
                        color: blok?.countdown[0]?.text_color,
                      }}
                    >
                      <Text
                        p
                        className="lp-countdown__text"
                        style={{
                          color: blok?.countdown[0]?.title_color
                            ? blok?.countdown[0]?.title_color
                            : blok?.countdown[0]?.text_color,
                        }}
                      >
                        <span
                          className="lp-countdown__text--bold"
                          style={{
                            color: blok?.countdown[0]?.title_bold_color
                              ? blok?.countdown[0]?.title_bold_color
                              : blok?.countdown[0]?.text_color,
                          }}
                        >
                          <If
                            condition={blok?.countdown[0]?.title_bold}
                            renderIf={blok?.countdown[0]?.title_bold}
                          />
                        </span>{' '}
                        <If
                          condition={blok?.countdown[0]?.title}
                          renderIf={blok?.countdown[0]?.title}
                        />
                      </Text>
                      <CountdownTimer targetDate={targetDate} separator=":" />
                    </div>
                  }
                />
              }
            />
          )}

          <div className="form-container">
            <Formik
              initialValues={{ document: '' }}
              onSubmit={(values) => {
                const documentNumber = values.document
                const leadType =
                  documentNumber &&
                  removeSpecialChars(documentNumber).length > 11
                    ? 'PJ'
                    : 'PF'
                storage.session.setItem('leadType', leadType)

                analytics.track('fieldInputted', {
                  fieldValue: btoa(documentNumber),
                  fieldLabel: 'cpf_home',
                })
                analytics.track('formStarted')
                handlePerformanceMark(ROUTES.register.pageName)
                navigate(ROUTES.register.path, {
                  state: {
                    document: values.document,
                    source_url: location.href,
                  },
                })
              }}
            >
              {({ ...formProps }) => {
                return (
                  <Form className="form-content">
                    <InputText
                      name="document"
                      placeholder="Digite seu CPF ou CNPJ"
                      mask={(e) => {
                        if (e && removeSpecialChars(e).length <= 11)
                          return cpfMask
                        else return cnpjMask
                      }}
                      rules={{ isCpfOrCnpj: true, isRequired: true }}
                      inputMode="numeric"
                      dataTest={
                        !isInCarrossel || firstBanner ? 'input-document' : ''
                      }
                      errorColor={blok?.error_text_color}
                      {...formProps}
                    />

                    <Button
                      className="button"
                      type="submit"
                      data-unique="63a2d907-4325-4e21-a45f-d420493b4f10"
                      data-test={
                        !isInCarrossel || firstBanner
                          ? 'button-submit-document'
                          : ''
                      }
                      style={{
                        backgroundColor: blok?.button_bg_color,
                        color: blok?.button_text_color,
                      }}
                      data-make-location="hero"
                      data-make-position="hero-cta"
                      data-make-actionoutcome="check_cpf"
                      data-make-element="button"
                      data-make-outboundurl={`${ROOT_URL}${ROUTES.register.path}`}
                    >
                      Consultar
                    </Button>
                  </Form>
                )
              }}
            </Formik>
          </div>
        </div>
      </div>

      {blok?.disclamer_text && (
        <Text
          p
          className={classNames('banner-campaign-text', {
            'banner-campaign-text--carrossel': isInCarrossel,
          })}
          style={{ color: blok?.disclamer_text_color }}
        >
          {blok?.disclamer_text}
        </Text>
      )}
    </>
  )
}

const LandingHeroHoliday = ({ blok, ...props }) => {
  const holidaySlides = blok?.holiday_slides || null

  if (!holidaySlides) return <></>

  if (holidaySlides.length === 1) {
    return <HeroBanner blok={holidaySlides[0]} {...props} />
  }

  return (
    <Carousel bullets arrows>
      {holidaySlides.map((slide, index) => (
        <HeroBanner
          key={slide._uid}
          firstBanner={index === 0}
          blok={slide}
          isInCarrossel
          {...props}
        />
      ))}
    </Carousel>
  )
}

export default LandingHeroHoliday
